import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppLayout from "../components/AppLayout";
import {
  Bell,
  BrainCircuit,
  Globe,
  Lock,
  Moon,
  RotateCcw,
  Save,
  ShieldCheck,
  Sun,
  User,
  UserPlus,
  AlertCircle,
} from "lucide-react";
import { supabase } from "../lib/supabase";
import { useAuth } from "../contexts/AuthContext";
import { canUseFullDetailAi } from "../lib/aiPrivacy";

function SectionCard({ title, description, children, isDark }) {
  return (
    <section
      className={`rounded-3xl border p-6 shadow-sm ${
        isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
      }`}
    >
      <div className="mb-5">
        <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
          {title}
        </h3>
        <p className={`mt-1 text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>
          {description}
        </p>
      </div>
      {children}
    </section>
  );
}

function Field({ label, hint, children, isDark }) {
  return (
    <div className="space-y-2">
      <div className="min-h-[2.75rem]">
        <p className={`text-sm font-medium ${isDark ? "text-slate-200" : "text-slate-800"}`}>
          {label}
        </p>
        {hint ? (
          <p className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>{hint}</p>
        ) : null}
      </div>
      {children}
    </div>
  );
}

function Input({ value, onChange, placeholder, isDark, type = "text", ...props }) {
  return (
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      {...props}
      className={`h-12 w-full rounded-2xl border px-4 text-sm outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100 ${
        isDark
          ? "border-slate-700 bg-slate-950 text-white placeholder:text-slate-500"
          : "border-slate-200 bg-white text-slate-900 placeholder:text-slate-400"
      }`}
    />
  );
}

function Select({ value, onChange, options, isDark }) {
  return (
    <select
      value={value}
      onChange={onChange}
      className={`h-12 w-full rounded-2xl border px-4 text-sm outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100 ${
        isDark
          ? "border-slate-700 bg-slate-950 text-white"
          : "border-slate-200 bg-white text-slate-900"
      }`}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

function Toggle({ checked, onChange, isDark }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative h-7 w-12 rounded-full transition ${
        checked ? "bg-sky-500" : isDark ? "bg-slate-700" : "bg-slate-300"
      }`}
    >
      <span
        className={`absolute top-1 h-5 w-5 rounded-full bg-white transition ${
          checked ? "left-6" : "left-1"
        }`}
      />
    </button>
  );
}

const defaultSettings = {
  fullName: "SpaceM8 Admin",
  email: "admin@spacem8.ai",
  company: "SpaceM8",
  timezone: "Africa/Nairobi",
  language: "en",
  emailAlerts: true,
  smsAlerts: false,
  aiRecommendations: true,
  riskAlerts: true,
  weeklyReports: true,
  aiExternalProcessingEnabled: true,
  aiMaskCustomerData: true,
  aiHighPrivacyMode: true,
  aiFullDetailMode: false,
};

export default function SettingsPage({
  isDark,
  setIsDark,
  currency,
  setCurrency,
}) {
  const navigate = useNavigate();
  const { user, profile, refreshProfile, sessionClaims } = useAuth();
  const userCanUseFullDetailAi = canUseFullDetailAi(profile?.role);
  const savedSettings = useMemo(() => {
    const raw = localStorage.getItem("spacem8_settings");
    return raw ? JSON.parse(raw) : defaultSettings;
  }, []);

  const savedCurrency = localStorage.getItem("spacem8_currency") || currency || "KES";
  const savedTheme = JSON.parse(localStorage.getItem("spacem8_theme") ?? JSON.stringify(isDark));

  const [settings, setSettings] = useState(savedSettings);
  const [draftCurrency, setDraftCurrency] = useState(savedCurrency);
  const [draftTheme, setDraftTheme] = useState(savedTheme);
  const [savedMessage, setSavedMessage] = useState("");

  useEffect(() => {
    if (!profile) return;

    setSettings((prev) => ({
      ...prev,
      fullName: profile.full_name || prev.fullName,
      email: profile.email || prev.email,
    }));
  }, [profile]);

  const updateSetting = (key, value) => {
    setSettings((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const hasChanges =
    JSON.stringify(settings) !== JSON.stringify(savedSettings) ||
    draftCurrency !== savedCurrency ||
    draftTheme !== savedTheme;

  const handleSave = async () => {
    localStorage.setItem("spacem8_settings", JSON.stringify(settings));
    localStorage.setItem("spacem8_currency", draftCurrency);
    localStorage.setItem("spacem8_theme", JSON.stringify(draftTheme));

    setCurrency(draftCurrency);
    setIsDark(draftTheme);

    if (user && supabase) {
      const { error } = await supabase.from("user_profiles").upsert({
        id: user.id,
        email: settings.email || user.email,
        full_name: settings.fullName,
        role: profile?.role || "Viewer",
        status: profile?.status || "Active",
        updated_at: new Date().toISOString(),
      });

      if (!error) {
        await refreshProfile();
      }
    }

    setSavedMessage("Settings saved");
    setTimeout(() => setSavedMessage(""), 2000);
  };

  const handleReset = () => {
    setSettings(defaultSettings);
    setDraftCurrency("KES");
    setDraftTheme(false);
    setSavedMessage("");
  };

  return (
    <AppLayout
      isDark={isDark}
      setIsDark={setIsDark}
      currency={currency}
      setCurrency={setCurrency}
      eyebrow="System Preferences"
      title="Settings"
      description="Manage platform preferences, user profile, notifications, AI behavior, and regional configuration."
      actions={
        <div className="flex items-center gap-3">
          <button
            onClick={handleReset}
            className={`inline-flex h-12 items-center justify-center rounded-2xl border px-5 font-medium transition ${
              isDark
                ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            <RotateCcw className="mr-2 h-4 w-4" />
            Reset
          </button>

          <button
            onClick={handleSave}
            disabled={!hasChanges}
            className={`inline-flex h-12 items-center justify-center rounded-2xl px-5 font-medium text-white transition ${
              hasChanges
                ? "bg-orange-500 hover:bg-orange-600"
                : "cursor-not-allowed bg-orange-300"
            }`}
          >
            <Save className="mr-2 h-4 w-4" />
            Save changes
          </button>
        </div>
      }
    >
      {hasChanges ? (
        <div
          className={`flex items-center gap-3 rounded-2xl border px-4 py-3 text-sm ${
            isDark
              ? "border-amber-800 bg-amber-950 text-amber-300"
              : "border-amber-200 bg-amber-50 text-amber-700"
          }`}
        >
          <AlertCircle className="h-4 w-4" />
          <span>You have unsaved changes.</span>
        </div>
      ) : null}

      {savedMessage ? (
        <div
          className={`rounded-2xl border px-4 py-3 text-sm ${
            isDark
              ? "border-emerald-800 bg-emerald-950 text-emerald-300"
              : "border-emerald-200 bg-emerald-50 text-emerald-700"
          }`}
        >
          {savedMessage}
        </div>
      ) : null}

      <div className="grid gap-6 xl:grid-cols-3">
        <div className="space-y-6 xl:col-span-2">
          <SectionCard
            title="Profile"
            description="Update your account and organization details."
            isDark={isDark}
          >
            <div className="grid gap-5 md:grid-cols-2">
              <Field label="Full name" isDark={isDark}>
                <Input
                  value={settings.fullName}
                  onChange={(e) => updateSetting("fullName", e.target.value)}
                  placeholder="Full name"
                  isDark={isDark}
                />
              </Field>

              <Field label="Email address" isDark={isDark}>
                <Input
                  value={settings.email}
                  onChange={(e) => updateSetting("email", e.target.value)}
                  placeholder="Email address"
                  isDark={isDark}
                  type="email"
                />
              </Field>

              <Field label="Company" isDark={isDark}>
                <Input
                  value={settings.company}
                  onChange={(e) => updateSetting("company", e.target.value)}
                  placeholder="Company"
                  isDark={isDark}
                />
              </Field>

              <Field label="Language" isDark={isDark}>
                <Select
                  value={settings.language}
                  onChange={(e) => updateSetting("language", e.target.value)}
                  isDark={isDark}
                  options={[
                    { value: "en", label: "English" },
                    { value: "sw", label: "Swahili" },
                  ]}
                />
              </Field>
            </div>
          </SectionCard>

          <SectionCard
            title="Appearance & Regional"
            description="Control how the platform looks and how values are displayed."
            isDark={isDark}
          >
            <div className="grid gap-5 md:grid-cols-2">
              <Field label="Theme mode" isDark={isDark}>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setDraftTheme(false)}
                    className={`inline-flex h-12 items-center rounded-2xl border px-4 text-sm font-medium transition ${
                      !draftTheme
                        ? "border-sky-300 bg-sky-50 text-sky-700"
                        : isDark
                        ? "border-slate-700 bg-slate-950 text-slate-300"
                        : "border-slate-200 bg-white text-slate-700"
                    }`}
                  >
                    <Sun className="mr-2 h-4 w-4" />
                    Light
                  </button>

                  <button
                    type="button"
                    onClick={() => setDraftTheme(true)}
                    className={`inline-flex h-12 items-center rounded-2xl border px-4 text-sm font-medium transition ${
                      draftTheme
                        ? "border-sky-300 bg-sky-50 text-sky-700"
                        : isDark
                        ? "border-slate-700 bg-slate-950 text-slate-300"
                        : "border-slate-200 bg-white text-slate-700"
                    }`}
                  >
                    <Moon className="mr-2 h-4 w-4" />
                    Dark
                  </button>
                </div>
              </Field>

              <Field label="Currency" isDark={isDark}>
                <Select
                  value={draftCurrency}
                  onChange={(e) => setDraftCurrency(e.target.value)}
                  isDark={isDark}
                  options={[
                    { value: "KES", label: "KES - Kenyan Shilling" },
                    { value: "USD", label: "USD - US Dollar" },
                  ]}
                />
              </Field>

              <Field label="Timezone" isDark={isDark}>
                <Select
                  value={settings.timezone}
                  onChange={(e) => updateSetting("timezone", e.target.value)}
                  isDark={isDark}
                  options={[
                    { value: "Africa/Nairobi", label: "Africa/Nairobi" },
                    { value: "UTC", label: "UTC" },
                    { value: "Europe/London", label: "Europe/London" },
                    { value: "America/New_York", label: "America/New_York" },
                  ]}
                />
              </Field>
            </div>
          </SectionCard>

          <SectionCard
            title="Notifications"
            description="Choose when the system should notify you."
            isDark={isDark}
          >
            <div className="space-y-5">
              {[
                {
                  label: "Email alerts",
                  hint: "Receive system updates and important portfolio notifications by email.",
                  checked: settings.emailAlerts,
                  key: "emailAlerts",
                },
                {
                  label: "SMS alerts",
                  hint: "Receive urgent payment and maintenance alerts on mobile.",
                  checked: settings.smsAlerts,
                  key: "smsAlerts",
                },
                {
                  label: "Weekly reports",
                  hint: "Get a weekly summary of revenue, occupancy, and risk signals.",
                  checked: settings.weeklyReports,
                  key: "weeklyReports",
                },
              ].map((item) => (
                <div
                  key={item.label}
                  className={`flex items-center justify-between rounded-2xl border p-4 ${
                    isDark ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50"
                  }`}
                >
                  <div className="pr-4">
                    <p className={`text-sm font-medium ${isDark ? "text-white" : "text-slate-900"}`}>
                      {item.label}
                    </p>
                    <p className={`mt-1 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                      {item.hint}
                    </p>
                  </div>
                  <Toggle
                    checked={item.checked}
                    onChange={(value) => updateSetting(item.key, value)}
                    isDark={isDark}
                  />
                </div>
              ))}
            </div>
          </SectionCard>

          <SectionCard
            title="AI Preferences"
            description="Control how the AI layer behaves across the platform."
            isDark={isDark}
          >
            <div className="space-y-5">
              {[ 
                {
                  label: "AI recommendations",
                  hint: "Show proactive operational recommendations on dashboards and pages.",
                  checked: settings.aiRecommendations,
                  key: "aiRecommendations",
                },
                {
                  label: "Risk alerts",
                  hint: "Highlight high-risk payment, occupancy, and maintenance signals.",
                  checked: settings.riskAlerts,
                  key: "riskAlerts",
                },
                {
                  label: "Allow external AI processing",
                  hint: "Permit the secure server-side assistant to send reduced SpaceM8 portfolio data to the configured AI provider.",
                  checked: settings.aiExternalProcessingEnabled,
                  key: "aiExternalProcessingEnabled",
                },
                {
                  label: "Mask customer identifiers",
                  hint: "Replace tenant names with aliases before any portfolio data is sent to the external AI assistant.",
                  checked: settings.aiMaskCustomerData,
                  key: "aiMaskCustomerData",
                },
                {
                  label: "High privacy mode",
                  hint: "Also mask property and unit references and replace raw maintenance text with generalized issue summaries.",
                  checked: settings.aiHighPrivacyMode,
                  key: "aiHighPrivacyMode",
                },
              ].map((item) => (
                <div
                  key={item.label}
                  className={`flex items-center justify-between rounded-2xl border p-4 ${
                    isDark ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50"
                  }`}
                >
                  <div className="pr-4">
                    <p className={`text-sm font-medium ${isDark ? "text-white" : "text-slate-900"}`}>
                      {item.label}
                    </p>
                    <p className={`mt-1 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                      {item.hint}
                    </p>
                  </div>
                  <Toggle
                    checked={item.checked}
                    onChange={(value) => updateSetting(item.key, value)}
                    isDark={isDark}
                  />
                </div>
              ))}

              <div
                className={`flex items-center justify-between rounded-2xl border p-4 ${
                  isDark ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50"
                }`}
              >
                <div className="pr-4">
                  <p className={`text-sm font-medium ${isDark ? "text-white" : "text-slate-900"}`}>
                    Full detail AI mode
                  </p>
                  <p className={`mt-1 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                    Growth and Administrator mode that allows identifiable customer data in secure AI requests when necessary.
                  </p>
                </div>
                <Toggle
                  checked={settings.aiFullDetailMode}
                  onChange={(value) =>
                    updateSetting("aiFullDetailMode", userCanUseFullDetailAi ? value : false)
                  }
                  isDark={isDark}
                />
              </div>

              {!userCanUseFullDetailAi ? (
                <div
                  className={`rounded-2xl border px-4 py-3 text-sm ${
                    isDark
                      ? "border-amber-800 bg-amber-950 text-amber-300"
                      : "border-amber-200 bg-amber-50 text-amber-700"
                  }`}
                >
                  Full detail AI mode is restricted to Growth and Administrator roles. Masked mode remains the recommended default.
                </div>
              ) : null}
            </div>
          </SectionCard>
        </div>

        <div className="space-y-6">
          <SectionCard
            title="Account"
            description="Your current admin profile and access level."
            isDark={isDark}
          >
            <div className="space-y-4">
              <div
                className={`rounded-2xl border p-4 ${
                  isDark ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50"
                }`}
              >
                <div className="mb-3 flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-full bg-gradient-to-br from-sky-500 to-orange-500 text-sm font-semibold text-white">
                    SM
                  </div>
                  <div>
                    <p className={`font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                      {settings.fullName}
                    </p>
                    <p className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                      Executive role
                    </p>
                  </div>
                </div>
              </div>

              {[
                { icon: User, label: "Profile completeness", value: "92%" },
                { icon: ShieldCheck, label: "Access level", value: profile?.role || "Viewer" },
                { icon: Globe, label: "Region", value: "Kenya" },
                {
                  icon: Bell,
                  label: "Notification mode",
                  value: settings.emailAlerts
                    ? settings.smsAlerts
                      ? "Email + SMS"
                      : "Email only"
                    : settings.smsAlerts
                    ? "SMS only"
                    : "Disabled",
                },
                {
                  icon: BrainCircuit,
                  label: "AI mode",
                  value: !settings.aiExternalProcessingEnabled
                    ? "Local only"
                    : settings.aiFullDetailMode
                    ? "Full detail"
                    : settings.aiHighPrivacyMode
                    ? "High privacy"
                    : settings.aiMaskCustomerData
                    ? "Masked"
                    : "Standard",
                },
                { icon: Lock, label: "Security status", value: "Protected" },
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <div
                    key={item.label}
                    className={`flex items-center gap-3 rounded-2xl border p-4 ${
                      isDark ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50"
                    }`}
                  >
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-sky-100 text-sky-700">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <p className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                        {item.label}
                      </p>
                      <p className={`text-sm font-medium ${isDark ? "text-white" : "text-slate-900"}`}>
                        {item.value}
                      </p>
                    </div>
                  </div>
                );
              })}

              {process.env.NODE_ENV !== "production" ? (
                <div
                  className={`rounded-2xl border p-4 ${
                    isDark ? "border-amber-800 bg-amber-950 text-amber-200" : "border-amber-200 bg-amber-50 text-amber-800"
                  }`}
                >
                  <p className="text-xs font-semibold uppercase tracking-[0.18em]">Session Diagnostics</p>
                  <div className="mt-3 space-y-2 text-xs leading-5">
                    <p>
                      <span className="font-semibold">Profile role:</span> {String(profile?.role || "Not set")}
                    </p>
                    <p>
                      <span className="font-semibold">User metadata role:</span>{" "}
                      {String(user?.user_metadata?.role || user?.user_metadata?.app_role || "Not set")}
                    </p>
                    <p>
                      <span className="font-semibold">App metadata role:</span>{" "}
                      {String(user?.app_metadata?.role || user?.app_metadata?.app_role || "Not set")}
                    </p>
                    <p>
                      <span className="font-semibold">Token app role:</span>{" "}
                      {String(sessionClaims?.app_metadata?.role || sessionClaims?.app_metadata?.app_role || "Not set")}
                    </p>
                    <p>
                      <span className="font-semibold">Token user role:</span>{" "}
                      {String(sessionClaims?.user_metadata?.role || sessionClaims?.user_metadata?.app_role || "Not set")}
                    </p>
                    <p>
                      <span className="font-semibold">Token auth role:</span> {String(sessionClaims?.role || "Not set")}
                    </p>
                    <p className={isDark ? "text-amber-300/80" : "text-amber-700"}>
                      These values come from the live browser session, which is what Supabase RLS evaluates.
                    </p>
                  </div>
                </div>
              ) : null}
            </div>
          </SectionCard>

          <SectionCard
            title="User Management"
            description="Organization bootstrap, login provisioning, and access activity now live in their own workspace."
            isDark={isDark}
          >
            <div className="space-y-4">
              <div
                className={`rounded-2xl border px-4 py-3 text-sm ${
                  isDark
                    ? "border-sky-800 bg-sky-950 text-sky-200"
                    : "border-sky-200 bg-sky-50 text-sky-800"
                }`}
              >
                Growth and Administrator tools for organization setup, staff logins, tenant access, and audit activity have moved out of Settings.
              </div>

              <button
                type="button"
                onClick={() => navigate("/user-management")}
                className="inline-flex h-12 items-center justify-center rounded-2xl bg-orange-500 px-5 font-medium text-white transition hover:bg-orange-600"
              >
                <UserPlus className="mr-2 h-4 w-4" />
                Open User Management
              </button>
            </div>
          </SectionCard>
        </div>
      </div>
    </AppLayout>
  );
}
